export interface PageSuggestion {
  /** Stable kebab-case id reported to analytics and the chat backend; never reuse one for different content. */
  id: string;
  /** i18n key for the chip label. */
  labelKey: string;
  /** i18n key for the question sent to OptiBot when the chip is clicked. */
  questionKey: string;
}

const CONTENT_NOT_SHOWING: PageSuggestion = {
  id: 'content-not-showing',
  labelKey: 'optibot.suggestion.contentNotShowing',
  questionKey: 'optibot.suggestionQuestion.contentNotShowing',
};

const BILLING_SUGGESTIONS: PageSuggestion[] = [
  {
    id: 'add-screens',
    labelKey: 'optibot.suggestion.addScreens',
    questionKey: 'optibot.suggestionQuestion.addScreens',
  },
  {
    id: 'explain-charge',
    labelKey: 'optibot.suggestion.explainCharge',
    questionKey: 'optibot.suggestionQuestion.explainCharge',
  },
  {
    id: 'invoices-payment-method',
    labelKey: 'optibot.suggestion.invoicesPaymentMethod',
    questionKey: 'optibot.suggestionQuestion.invoicesPaymentMethod',
  },
];

/**
 * Map of page URL patterns to contextual suggestions, each list ranked by how often customers ask it.
 * Keys are substrings matched against the current URL pathname.
 */
const PAGE_SUGGESTIONS: Record<string, PageSuggestion[]> = {
  screenManagement: [
    CONTENT_NOT_SHOWING,
    {
      id: 'screen-offline',
      labelKey: 'optibot.suggestion.whyScreenOffline',
      questionKey: 'optibot.suggestionQuestion.whyScreenOffline',
    },
    {
      id: 'assign-content-to-screen',
      labelKey: 'optibot.suggestion.assignContentToScreen',
      questionKey: 'optibot.suggestionQuestion.assignContentToScreen',
    },
  ],
  assetManagement: [
    {
      id: 'office-file-sync',
      labelKey: 'optibot.suggestion.officeFileSync',
      questionKey: 'optibot.suggestionQuestion.officeFileSync',
    },
    {
      id: 'show-website-youtube',
      labelKey: 'optibot.suggestion.showWebsiteYoutube',
      questionKey: 'optibot.suggestionQuestion.showWebsiteYoutube',
    },
    {
      id: 'upload-assets',
      labelKey: 'optibot.suggestion.uploadAssets',
      questionKey: 'optibot.suggestionQuestion.uploadAssets',
    },
  ],
  playlistManagement: [
    {
      id: 'create-playlist',
      labelKey: 'optibot.suggestion.createPlaylist',
      questionKey: 'optibot.suggestionQuestion.createPlaylist',
    },
    {
      id: 'add-web-or-video-to-playlist',
      labelKey: 'optibot.suggestion.addWebOrVideoToPlaylist',
      questionKey: 'optibot.suggestionQuestion.addWebOrVideoToPlaylist',
    },
    {
      id: 'playback-control',
      labelKey: 'optibot.suggestion.playbackControl',
      questionKey: 'optibot.suggestionQuestion.playbackControl',
    },
  ],
  scheduleManagement: [
    {
      id: 'create-schedule',
      labelKey: 'optibot.suggestion.createSchedule',
      questionKey: 'optibot.suggestionQuestion.createSchedule',
    },
    {
      id: 'schedule-time-window',
      labelKey: 'optibot.suggestion.scheduleTimeWindow',
      questionKey: 'optibot.suggestionQuestion.scheduleTimeWindow',
    },
    {
      id: 'tv-power-schedule',
      labelKey: 'optibot.suggestion.tvPowerSchedule',
      questionKey: 'optibot.suggestionQuestion.tvPowerSchedule',
    },
  ],
  templateManagement: [
    {
      id: 'use-templates',
      labelKey: 'optibot.suggestion.useTemplates',
      questionKey: 'optibot.suggestionQuestion.useTemplates',
    },
    {
      id: 'template-data-source',
      labelKey: 'optibot.suggestion.templateDataSource',
      questionKey: 'optibot.suggestionQuestion.templateDataSource',
    },
    {
      id: 'template-split-layout',
      labelKey: 'optibot.suggestion.templateSplitLayout',
      questionKey: 'optibot.suggestionQuestion.templateSplitLayout',
    },
  ],
  designManagement: [
    {
      id: 'design-data-source-repeater',
      labelKey: 'optibot.suggestion.designDataSourceRepeater',
      questionKey: 'optibot.suggestionQuestion.designDataSourceRepeater',
    },
    {
      id: 'resize-crop-rotate',
      labelKey: 'optibot.suggestion.resizeCropRotate',
      questionKey: 'optibot.suggestionQuestion.resizeCropRotate',
    },
    {
      id: 'duplicate-design',
      labelKey: 'optibot.suggestion.duplicateDesign',
      questionKey: 'optibot.suggestionQuestion.duplicateDesign',
    },
  ],
  engageManagement: [
    {
      id: 'engage-interactive-kiosk',
      labelKey: 'optibot.suggestion.engageInteractiveKiosk',
      questionKey: 'optibot.suggestionQuestion.engageInteractiveKiosk',
    },
    {
      id: 'engage-add-website',
      labelKey: 'optibot.suggestion.engageAddWebsite',
      questionKey: 'optibot.suggestionQuestion.engageAddWebsite',
    },
    {
      id: 'engage-edit-app',
      labelKey: 'optibot.suggestion.engageEditApp',
      questionKey: 'optibot.suggestionQuestion.engageEditApp',
    },
  ],
  'subscription-plan': BILLING_SUGGESTIONS,
  '/s/payment': BILLING_SUGGESTIONS,
  'order-device': BILLING_SUGGESTIONS,
  'account-members': [
    {
      id: 'add-user',
      labelKey: 'optibot.suggestion.addUser',
      questionKey: 'optibot.suggestionQuestion.addUser',
    },
    {
      id: 'manage-teams',
      labelKey: 'optibot.suggestion.manageTeams',
      questionKey: 'optibot.suggestionQuestion.manageTeams',
    },
    {
      id: 'change-account-owner',
      labelKey: 'optibot.suggestion.changeAccountOwner',
      questionKey: 'optibot.suggestionQuestion.changeAccountOwner',
    },
  ],
  '/app/mdm': [
    {
      id: 'device-limit',
      labelKey: 'optibot.suggestion.deviceLimit',
      questionKey: 'optibot.suggestionQuestion.deviceLimit',
    },
    {
      id: 'reconnect-offline-device',
      labelKey: 'optibot.suggestion.reconnectOfflineDevice',
      questionKey: 'optibot.suggestionQuestion.reconnectOfflineDevice',
    },
    {
      id: 'update-firmware',
      labelKey: 'optibot.suggestion.updateFirmware',
      questionKey: 'optibot.suggestionQuestion.updateFirmware',
    },
  ],
  '/app/playbackReport': [
    {
      id: 'playback-history',
      labelKey: 'optibot.suggestion.playbackHistory',
      questionKey: 'optibot.suggestionQuestion.playbackHistory',
    },
    {
      id: 'add-asset-to-playback-report',
      labelKey: 'optibot.suggestion.addAssetToPlaybackReport',
      questionKey: 'optibot.suggestionQuestion.addAssetToPlaybackReport',
    },
    {
      id: 'playback-retention',
      labelKey: 'optibot.suggestion.playbackRetention',
      questionKey: 'optibot.suggestionQuestion.playbackRetention',
    },
  ],
  '/app/ai-analytics': [
    {
      id: 'ai-analytics-what',
      labelKey: 'optibot.suggestion.aiAnalyticsWhat',
      questionKey: 'optibot.suggestionQuestion.aiAnalyticsWhat',
    },
    {
      id: 'ai-analytics-empty',
      labelKey: 'optibot.suggestion.aiAnalyticsEmpty',
      questionKey: 'optibot.suggestionQuestion.aiAnalyticsEmpty',
    },
    {
      id: 'ai-analytics-plan',
      labelKey: 'optibot.suggestion.aiAnalyticsPlan',
      questionKey: 'optibot.suggestionQuestion.aiAnalyticsPlan',
    },
  ],
};

const DEFAULT_SUGGESTIONS: PageSuggestion[] = [
  {
    id: 'billing-plan',
    labelKey: 'optibot.suggestion.billingPlan',
    questionKey: 'optibot.suggestionQuestion.billingPlan',
  },
  CONTENT_NOT_SHOWING,
  {
    id: 'create-assign-content',
    labelKey: 'optibot.suggestion.howToContent',
    questionKey: 'optibot.suggestionQuestion.howToContent',
  },
];

/**
 * Get contextual suggestions based on the current page URL.
 */
export function getPageSuggestions(pathname: string): PageSuggestion[] {
  for (const [pattern, suggestions] of Object.entries(PAGE_SUGGESTIONS)) {
    if (pathname.includes(pattern)) {
      return suggestions;
    }
  }
  return DEFAULT_SUGGESTIONS;
}
